/**
 * PrefacturarModal - Modal para prefacturar storages
 *
 * Muestra los filtros aplicados, resumen de storages
 * y permite confirmar la prefacturación
 *
 * Las filterOptions se cargan desde la configuración centralizada
 * @see src/modules/Invoices/EligibleStorage/config/filterOptions.ts
 */
import { Button } from '@shared/components/ui/button';
import { ModalCenter } from '@shared/components/ui/ModalCenter';
import { Badge } from '@shared/components/ui/badge';
import { ModalCenterRef } from '@shared/components/ui/ModalCenter/types';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterConditionOutput, FilterStructure } from '@components/ui/AdvancedFilter';
import { Filter, FileCheck2, Package, ArrowRight, Sparkles } from 'lucide-react';

export interface PrefacturarModalRef {
  open: (data: PrefacturarModalData) => void;
  close: () => void;
}

export interface PrefacturarModalData {
  totalResults: number; // Total de storages según filtros aplicados
  filters: FilterConditionOutput[];
  filterOptions: FilterStructure[]; // 🎯 Opciones de filtros para traducir labels
  onConfirm: () => void;
}

export const PrefacturarModal = forwardRef<PrefacturarModalRef>((_, ref) => {
  const { t } = useTranslation('eligibleStorageModule');
  const { t: tUI } = useTranslation('ui');
  const modalRef = useRef<ModalCenterRef>(null);

  // 🎯 Usar useState para que el modal se re-renderice con los datos actualizados
  const [modalData, setModalData] = useState<PrefacturarModalData>({
    totalResults: 0,
    filters: [],
    filterOptions: [],
    onConfirm: () => {},
  });

  useImperativeHandle(
    ref,
    () => ({
      open: (data: PrefacturarModalData) => {
        setModalData(data);
        modalRef.current?.openModal();
      },
      close: () => {
        modalRef.current?.closeModal();
      },
    }),
    [],
  );

  const handleConfirm = () => {
    modalData.onConfirm();
  };

  const { totalResults, filters } = modalData;

  const formatDateValue = (value: string): string => {
    if (!value) {
      return value;
    }

    const asDate = new Date(value);
    if (!Number.isNaN(asDate.getTime())) {
      const day = String(asDate.getDate()).padStart(2, '0');
      const month = String(asDate.getMonth() + 1).padStart(2, '0');
      const year = String(asDate.getFullYear());
      return `${day}/${month}/${year}`;
    }

    const isoDateMatch = /^\d{4}-\d{2}-\d{2}$/.exec(value);
    if (isoDateMatch) {
      const [year, month, day] = value.split('-');
      return `${day}/${month}/${year}`;
    }

    return value;
  };

  // 🎯 Función para obtener el label legible de un filtro
  const getFilterLabel = (filter: FilterConditionOutput) => {
    const selectedFilter = modalData.filterOptions.find((f) => f.column.value === filter.column);
    const selectedAction = selectedFilter?.actions.find((a) => a.value === filter.action);

    const columnLabel = selectedFilter?.column.label || filter.column;
    const actionLabel = selectedAction?.label || tUI(`filterOperators.${filter.action}`, filter.action);

    const isDateColumn = selectedFilter?.column.type === 'date' || selectedFilter?.column.type === 'date-range';

    const fallbackValue = (() => {
      if (filter.value === undefined || filter.value === null) {
        return '';
      }
      if (Array.isArray(filter.value)) {
        return filter.value
          .map((valueItem) => (isDateColumn ? formatDateValue(String(valueItem)) : String(valueItem)))
          .join(', ');
      }
      const asString = String(filter.value);
      return isDateColumn ? formatDateValue(asString) : asString;
    })();

    return {
      columnLabel,
      actionLabel,
      valueLabel: fallbackValue,
    };
  };

  // 🎯 Renderizar los filtros aplicados como tags
  const renderFilterTags = () => {
    if (!filters || filters.length === 0) {
      return (
        <div className="flex items-center gap-2 py-3 px-4 rounded-lg bg-muted/30 border border-dashed border-card-border">
          <Filter className="w-4 h-4 text-complementary-text" />
          <p className="text-sm text-complementary-text italic">
            {t('modals.prefacturar.no_filters', 'No hay filtros aplicados')}
          </p>
        </div>
      );
    }

    return (
      <div className="flex flex-wrap gap-2">
        {filters.map((filter, index) => {
          const { columnLabel, actionLabel, valueLabel } = getFilterLabel(filter);

          return (
            <div
              key={index}
              className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg 
                         bg-gradient-to-r from-card-bg to-muted/50 
                         border border-card-border/60 
                         shadow-sm hover:shadow-md hover:border-primary/30
                         transition-all duration-200 ease-out"
            >
              <span className="text-xs font-medium text-foreground/80">{columnLabel}</span>
              <ArrowRight className="w-3 h-3 text-primary/60" />
              <span className="text-xs text-primary font-semibold">{actionLabel}</span>
              <span className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium max-w-[180px] truncate">
                {valueLabel}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <ModalCenter
      ref={modalRef}
      title={t('modals.prefacturar.title', 'Prefacturación')}
      type="default"
      width="lg"
      data-testid="prefacturar-modal"
      icon={<FileCheck2 className="w-5 h-5 text-primary" />}
    >
      <div className="max-h-[calc(100vh-8rem)] overflow-y-auto">
        <div className="space-y-6">
          {/* Sección: Filtros aplicados */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-md bg-primary/10">
                <Filter className="w-4 h-4 text-primary" />
              </div>
              <h3 className="font-semibold text-foreground">
                {t('modals.prefacturar.filters_title', 'Filtros aplicados')}
              </h3>
              {filters.length > 0 && (
                <Badge className="bg-primary/10 text-primary border-0 text-xs font-semibold">{filters.length}</Badge>
              )}
            </div>
            {renderFilterTags()}
          </div>

          {/* Sección: Resumen con diseño mejorado */}
          <div className="relative overflow-hidden rounded-xl border border-card-border bg-gradient-to-br from-card-bg via-card-bg to-primary/5 p-5">
            {/* Decoración de fondo */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary/10 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-success/10 to-transparent rounded-full translate-y-1/2 -translate-x-1/2" />

            <div className="relative space-y-4">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-md bg-success/10">
                  <Sparkles className="w-4 h-4 text-success" />
                </div>
                <h3 className="font-semibold text-foreground">{t('modals.prefacturar.summary_title', 'Resumen')}</h3>
              </div>

              {/* Grid de estadísticas */}
              <div className="grid grid-cols-2 gap-4">
                {/* Storages */}
                <div className="flex items-center gap-3 p-3 rounded-lg bg-background/60 border border-card-border/50">
                  <div className="p-2 rounded-lg bg-info/10">
                    <Package className="w-5 h-5 text-info" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground tabular-nums">{totalResults.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">
                      {totalResults === 1
                        ? t('modals.prefacturar.storage_singular', 'almacenamiento')
                        : t('modals.prefacturar.storage_plural', 'almacenamientos')}{' '}
                      {totalResults === 1
                        ? t('modals.prefacturar.ready_singular', 'listo')
                        : t('modals.prefacturar.ready_plural', 'listos')}
                    </p>
                  </div>
                </div>

                {/* Filtros */}
                <div className="flex items-center gap-3 p-3 rounded-lg bg-background/60 border border-card-border/50">
                  <div className="p-2 rounded-lg bg-success/10">
                    <Filter className="w-5 h-5 text-success" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground tabular-nums">{filters.length}</p>
                    <p className="text-xs text-muted-foreground">
                      {filters.length === 1
                        ? t('modals.prefacturar.filter_singular', 'filtro')
                        : t('modals.prefacturar.filter_plural', 'filtros')}{' '}
                      {filters.length === 1
                        ? t('modals.prefacturar.applied_singular', 'aplicado')
                        : t('modals.prefacturar.applied_plural', 'aplicados')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer con botón de acción */}
          <div className="flex justify-end gap-3 pt-2 border-t border-card-border/50">
            <Button
              variant="primary"
              size="lg"
              onClick={handleConfirm}
              data-testid="prefacturar-modal-confirm"
              className="gap-2 shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-200"
            >
              <FileCheck2 className="w-4 h-4" />
              {t('modals.prefacturar.button', 'Prefacturar')}
              <Badge className="bg-white/20 text-white border-0 text-xs font-bold ml-1">
                {totalResults.toLocaleString()}
              </Badge>
            </Button>
          </div>
        </div>
      </div>
    </ModalCenter>
  );
});

PrefacturarModal.displayName = 'PrefacturarModal';
